import React, { useState, useCallback, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiChevronUp,
  FiChevronDown,
  FiMessageCircle,
  FiEye,
  FiClock,
  FiMoreHorizontal,
  FiUser,
  FiHash,
  FiBookmark,
  FiShare2,
  FiBell,
  FiBellOff,
  FiAlertTriangle,
  FiAward,
  FiCheck,
  FiImage,
  FiZoomIn,
} from "react-icons/fi";
import {
  HiOutlineFire as FiFlame,
  HiOutlineTrendingUp as FiTrending,
} from "react-icons/hi";
import {
  RiPushpinFill as PinIcon,
  RiLockFill as LockIcon,
  RiBookmarkFill as BookmarkFillIcon,
} from "react-icons/ri";
import { toast } from "react-toastify";
import communityService from "../../../services/communityService";

// Award Components
import { AwardBadges, AwardSummary } from "./awards";

// Image Lightbox
import ImageLightbox from "./ImageLightbox";

// Styles
import "./tc-variables.css";
import "./ThreadCard.css";

// Lazy load modals
const ReportThreadModal = lazy(() => import("./ReportThreadModal"));
const AwardThreadModal = lazy(() => import("./AwardThreadModal"));
const SetFlairModal = lazy(() => import("./SetFlairModal"));
const CrossPostModal = lazy(() => import("./CrossPostModal"));
const EditThreadModal = lazy(() => import("./EditThreadModal"));

// ============================================
// ANIMATION VARIANTS
// ============================================

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const buttonVariants = {
  tap: { scale: 0.92 },
  hover: { scale: 1.08 },
};

const dropdownVariants = {
  hidden: { opacity: 0, y: -6, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.12, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -6,
    scale: 0.96,
    transition: { duration: 0.08 },
  },
};

const badgeVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 500, damping: 25 },
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * VoteSection - Upvote/Downvote controls
 */
const VoteSection = React.memo(
  ({
    score,
    userHasUpvoted,
    userHasDownvoted,
    isHot,
    isTrending,
    onUpvote,
    onDownvote,
  }) => {
    const scoreClass =
      score > 0 ? "tc-positive" : score < 0 ? "tc-negative" : "tc-zero";

    return (
      <div className="tc-votes">
        <motion.button
          className={`tc-vote-btn tc-up ${userHasUpvoted ? "tc-voted" : ""}`}
          onClick={onUpvote}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          title="Upvote"
          aria-label="Upvote"
        >
          <FiChevronUp size={20} strokeWidth={2.5} />
        </motion.button>

        <motion.span
          className={`tc-score ${scoreClass}`}
          key={score}
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.15 }}
        >
          {score}
        </motion.span>

        <motion.button
          className={`tc-vote-btn tc-down ${
            userHasDownvoted ? "tc-voted" : ""
          }`}
          onClick={onDownvote}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          title="Downvote"
          aria-label="Downvote"
        >
          <FiChevronDown size={20} strokeWidth={2.5} />
        </motion.button>

        <div className="tc-badges">
          <AnimatePresence>
            {isHot && (
              <motion.div
                className="tc-badge tc-hot"
                variants={badgeVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                title="Hot"
              >
                <FiFlame size={14} />
              </motion.div>
            )}
            {isTrending && (
              <motion.div
                className="tc-badge tc-trending"
                variants={badgeVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                title="Trending"
              >
                <FiTrending size={14} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }
);

VoteSection.displayName = "VoteSection";

/**
 * CommunityBadge - Shows community info in feed
 */
const CommunityBadge = React.memo(({ community, onCommunityClick }) => {
  const photoUrl = community?.signedPhotoURL || community?.photoURL;

  const handleClick = useCallback((e) => {
    e.stopPropagation(); // Prevent card click from firing
    if (onCommunityClick && community) {
      onCommunityClick(community);
    }
  }, [community, onCommunityClick]);

  if (!community) return null;

  return (
    <motion.div
      className="tc-community"
      onClick={handleClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{ cursor: onCommunityClick ? 'pointer' : 'default' }}
    >
      <div className="tc-community-avatar">
        {photoUrl ? (
          <img src={photoUrl} alt={community.name} />
        ) : (
          <div className="tc-community-avatar-text">
            {community.name?.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <span className="tc-community-name">{community.name}</span>
    </motion.div>
  );
});

CommunityBadge.displayName = "CommunityBadge";

/**
 * ThreadHeader - Author, timestamp, menu
 */
const ThreadHeader = React.memo(
  ({
    thread,
    profileImage,
    formatTimeAgo,
    isModeratorOrCreator,
    showActions,
    onToggleActions,
    onPin,
    onLock,
  }) => (
    <div className="tc-header">
      <div className="tc-meta">
        {thread.isPinned && (
          <div className="tc-status tc-pin">
            <PinIcon size={12} />
          </div>
        )}
        {thread.status === "locked" && (
          <div className="tc-status tc-lock">
            <LockIcon size={12} />
          </div>
        )}

        <div className="tc-author">
          <div className="tc-author-avatar">
            {profileImage ? (
              <img src={profileImage} alt={thread.author?.name} />
            ) : (
              <FiUser size={14} />
            )}
          </div>
          <span className="tc-author-name">{thread.author?.name}</span>
        </div>

        <span className="tc-time">
          <FiClock size={11} />
          {formatTimeAgo(thread.createdAt)}
        </span>

        {thread.lastActivity !== thread.createdAt && (
          <span className="tc-activity">
            • updated {formatTimeAgo(thread.lastActivity)}
          </span>
        )}
      </div>

      {isModeratorOrCreator && (
        <div className="tc-menu-wrap">
          <motion.button
            className="tc-menu-btn"
            onClick={onToggleActions}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="More options"
          >
            <FiMoreHorizontal size={18} />
          </motion.button>

          <AnimatePresence>
            {showActions && (
              <motion.div
                className="tc-dropdown tc-open"
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <button
                  className={`tc-dropdown-item ${
                    thread.isPinned ? "tc-active" : ""
                  }`}
                  onClick={onPin}
                >
                  <PinIcon size={14} />
                  {thread.isPinned ? "Unpin" : "Pin"}
                </button>
                <button
                  className={`tc-dropdown-item ${
                    thread.status === "locked" ? "tc-active" : ""
                  }`}
                  onClick={onLock}
                >
                  <LockIcon size={14} />
                  {thread.status === "locked" ? "Unlock" : "Lock"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
);

ThreadHeader.displayName = "ThreadHeader";

/**
 * ThreadPhoto - Image with error handling and click to expand
 */
const ThreadPhoto = React.memo(({ photoUrl, getValidPhotoUrl, onImageClick }) => {
  const [hasError, setHasError] = useState(false);
  const [retried, setRetried] = useState(false);

  const handleError = useCallback(
    (e) => {
      if (!retried) {
        setRetried(true);
        const cleanUrl = getValidPhotoUrl(photoUrl).split("?")[0];
        e.target.src = cleanUrl + "?t=" + Date.now();
      } else {
        setHasError(true);
      }
    },
    [photoUrl, getValidPhotoUrl, retried]
  );

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation(); // Prevent card click
      if (onImageClick) {
        onImageClick(getValidPhotoUrl(photoUrl));
      }
    },
    [onImageClick, photoUrl, getValidPhotoUrl]
  );

  if (hasError) {
    return (
      <div className="tc-photo">
        <div className="tc-photo-error">
          <FiImage size={24} />
          <span>Unable to load image</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="tc-photo tc-photo-clickable"
      onClick={handleClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <img
        src={getValidPhotoUrl(photoUrl)}
        alt="Thread attachment"
        loading="lazy"
        onError={handleError}
      />
      <div className="tc-photo-overlay">
        <FiZoomIn size={20} />
        <span>Click to expand</span>
      </div>
    </motion.div>
  );
});

ThreadPhoto.displayName = "ThreadPhoto";

/**
 * ThreadTags - Tag pills
 */
const ThreadTags = React.memo(({ tags }) => {
  if (!tags || tags.length === 0) return null;

  return (
    <div className="tc-tags">
      {tags.map((tag, i) => (
        <motion.span
          key={i}
          className="tc-tag"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FiHash size={10} />
          {tag}
        </motion.span>
      ))}
    </div>
  );
});

ThreadTags.displayName = "ThreadTags";

/**
 * ShareButton - Copy link functionality
 */
const ShareButton = React.memo(({ communityId, threadId }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(
    async (e) => {
      e.stopPropagation();
      const url = `${window.location.origin}/communities/${communityId}/threads/${threadId}`;

      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied!");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.cssText = "position:fixed;left:-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
        toast.success("Link copied!");
        setTimeout(() => setCopied(false), 2000);
      }
    },
    [communityId, threadId]
  );

  return (
    <motion.button
      className="tc-action tc-share"
      onClick={handleShare}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.92 }}
      title="Share"
      aria-label="Share"
    >
      {copied ? <FiCheck size={15} /> : <FiShare2 size={15} />}
    </motion.button>
  );
});

ShareButton.displayName = "ShareButton";

/**
 * FollowButton - Follow/unfollow thread
 */
const FollowButton = React.memo(
  ({ communityId, threadId, initialIsFollowing }) => {
    const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
    const [loading, setLoading] = useState(false);

    const handleFollow = useCallback(
      async (e) => {
        e.stopPropagation();
        if (loading) return;

        const prev = isFollowing;
        setIsFollowing(!isFollowing);
        setLoading(true);

        try {
          const res = await communityService.followThread(
            communityId,
            threadId
          );
          if (res.success) {
            setIsFollowing(res.data.isFollowing);
            toast.success(res.data.isFollowing ? "Following" : "Unfollowed");
          } else {
            throw new Error();
          }
        } catch {
          setIsFollowing(prev);
          toast.error("Failed to update");
        } finally {
          setLoading(false);
        }
      },
      [communityId, threadId, isFollowing, loading]
    );

    return (
      <motion.button
        className={`tc-action tc-follow ${isFollowing ? "tc-following" : ""}`}
        onClick={handleFollow}
        disabled={loading}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.92 }}
        title={isFollowing ? "Unfollow" : "Follow"}
        aria-label={isFollowing ? "Unfollow" : "Follow"}
      >
        {loading ? (
          <div className="tc-spinner" />
        ) : isFollowing ? (
          <FiBellOff size={15} />
        ) : (
          <FiBell size={15} />
        )}
      </motion.button>
    );
  }
);

FollowButton.displayName = "FollowButton";

/**
 * ActionsRow - Action buttons and engagement chips
 */
const ActionsRow = React.memo(
  ({
    communityId,
    threadId,
    isFollowing,
    upvoteCount,
    replyCount,
    onAward,
    onReport,
  }) => (
    <div className="tc-actions">
      <ShareButton communityId={communityId} threadId={threadId} />
      <FollowButton
        communityId={communityId}
        threadId={threadId}
        initialIsFollowing={isFollowing}
      />
      <motion.button
        className="tc-action tc-award"
        onClick={onAward}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.92 }}
        title="Award"
        aria-label="Give award"
      >
        <FiAward size={15} />
      </motion.button>
      <motion.button
        className="tc-action tc-report"
        onClick={onReport}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.92 }}
        title="Report"
        aria-label="Report"
      >
        <FiAlertTriangle size={15} />
      </motion.button>

      <div className="tc-engagement">
        {upvoteCount > 0 && (
          <div className="tc-chip tc-upvotes">
            <FiChevronUp size={12} />
            <span>{upvoteCount}</span>
          </div>
        )}
        {replyCount > 0 && (
          <div className="tc-chip tc-replies">
            <FiMessageCircle size={12} />
            <span>{replyCount}</span>
          </div>
        )}
      </div>
    </div>
  )
);

ActionsRow.displayName = "ActionsRow";

// ============================================
// MAIN COMPONENT
// ============================================

const ThreadCard = ({
  thread,
  communityId: communityIdProp,
  onThreadClick,
  onCommunityClick,
  onVote,
  onDownvote,
  onSave,
  onPin,
  onLock,
  onThreadUpdated,
  onThreadDeleted,
  isModeratorOrCreator,
  canInteract = true,
  isReadOnly = false,
  formatTimeAgo,
  showCommunityInfo = false,
}) => {
  // Derive communityId from prop or thread.community for unified feed support
  const communityId = communityIdProp || thread.community?._id || thread.community;

  // State
  const [showActions, setShowActions] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showFlairModal, setShowFlairModal] = useState(false);
  const [showCrossPostModal, setShowCrossPostModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);

  // Utilities
  const getValidPhotoUrl = useCallback((url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/")) {
      return `https://ResearchPro.s3.ap-south-1.amazonaws.com${url}`;
    }
    return url;
  }, []);

  const getProfileImage = useCallback(() => {
    return (
      thread.author?.signedProfilePhotoUrl || thread.author?.profilePhotoUrl
    );
  }, [thread.author]);

  const truncate = useCallback((text, max = 200) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "..." : text;
  }, []);

  const isHot = useCallback(() => {
    const hrs = (Date.now() - new Date(thread.lastActivity)) / 3600000;
    return hrs < 24 && (thread.upvoteCount > 5 || thread.replyCount > 10);
  }, [thread.lastActivity, thread.upvoteCount, thread.replyCount]);

  const isTrending = useCallback(() => {
    const hrs = (Date.now() - new Date(thread.createdAt)) / 3600000;
    return hrs < 48 && thread.upvoteCount > 10;
  }, [thread.createdAt, thread.upvoteCount]);

  // Handlers
  const handleVote = useCallback(
    (e) => {
      e.stopPropagation();
      onVote?.(thread._id);
    },
    [onVote, thread._id]
  );

  const handleDownvote = useCallback(
    (e) => {
      e.stopPropagation();
      onDownvote?.(thread._id);
    },
    [onDownvote, thread._id]
  );

  const handleSave = useCallback(
    (e) => {
      e.stopPropagation();
      onSave?.(thread);
    },
    [onSave, thread]
  );

  const handlePin = useCallback(
    (e) => {
      e.stopPropagation();
      onPin?.(thread._id);
      setShowActions(false);
    },
    [onPin, thread._id]
  );

  const handleLock = useCallback(
    (e) => {
      e.stopPropagation();
      onLock?.(thread._id);
      setShowActions(false);
    },
    [onLock, thread._id]
  );

  const handleMenuToggle = useCallback((e) => {
    e.stopPropagation();
    setShowActions((prev) => !prev);
  }, []);


  const handleAward = useCallback((e) => {
    e.stopPropagation();
    setShowAwardModal(true);
  }, []);

  const handleAwardBadgeClick = useCallback((awardType) => {
    // Called from AwardBadges component - receives award type string, not event
    // AwardBadges already handles stopPropagation internally
    setShowAwardModal(true);
  }, []);

  const handleReport = useCallback((e) => {
    e.stopPropagation();
    setShowReportModal(true);
  }, []);

  const handleImageClick = useCallback((imageUrl) => {
    setLightboxImageUrl(imageUrl);
    setShowImageLightbox(true);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setShowImageLightbox(false);
    setLightboxImageUrl(null);
  }, []);

  // Classes
  const cardClass = [
    "tc-card",
    thread.isPinned && "tc-pinned",
    thread.status === "locked" && "tc-locked",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <motion.article
        className={cardClass}
        onClick={() => onThreadClick(thread)}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        layout
        role="article"
        aria-label={`Thread: ${thread.title}`}
      >
        {/* Vote Section */}
        <VoteSection
          score={thread.score}
          userHasUpvoted={thread.userHasUpvoted}
          userHasDownvoted={thread.userHasDownvoted}
          isHot={isHot()}
          isTrending={isTrending()}
          onUpvote={handleVote}
          onDownvote={handleDownvote}
        />

        {/* Body */}
        <div className="tc-body">
          {/* Community Badge */}
          {showCommunityInfo && (
            <CommunityBadge 
              community={thread.community} 
              onCommunityClick={onCommunityClick}
            />
          )}

          {/* Header */}
          <ThreadHeader
            thread={thread}
            profileImage={getProfileImage()}
            formatTimeAgo={formatTimeAgo}
            isModeratorOrCreator={isModeratorOrCreator}
            showActions={showActions}
            onToggleActions={handleMenuToggle}
            onPin={handlePin}
            onLock={handleLock}
          />

          {/* Main Content */}
          <div className="tc-main">
            <div className="tc-title-row">
              <h3 className="tc-title">{thread.title}</h3>
              {thread.flair && (
                <span
                  className="tc-flair"
                  style={{
                    color: thread.flair.color,
                    backgroundColor: thread.flair.backgroundColor,
                  }}
                >
                  {thread.flair.text}
                </span>
              )}
            </div>

            {thread.description && (
              <p className="tc-desc">{truncate(thread.description)}</p>
            )}

            {thread.photoUrl && (
              <ThreadPhoto
                photoUrl={thread.photoUrl}
                getValidPhotoUrl={getValidPhotoUrl}
                onImageClick={handleImageClick}
              />
            )}

            <ThreadTags tags={thread.tags} />

            {/* Award Badges - Per AWARDS-FRONTEND-GUIDE.md */}
            {thread.totalReactions > 0 && (
              <AwardBadges
                reactionCounts={thread.reactionCounts}
                totalReactions={thread.totalReactions}
                size="sm"
                onClick={handleAwardBadgeClick}
              />
            )}
          </div>

          {/* Footer */}
          <div className="tc-footer">
            <div className="tc-stats">
              <div className="tc-stat">
                <FiMessageCircle size={14} />
                <span>
                  {thread.replyCount}{" "}
                  {thread.replyCount === 1 ? "reply" : "replies"}
                </span>
              </div>
              <div className="tc-stat">
                <FiEye size={14} />
                <span>{thread.viewCount} views</span>
              </div>
              {onSave && canInteract && (
                <motion.button
                  className={`tc-save ${thread.isSaved ? "tc-saved" : ""}`}
                  onClick={handleSave}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={thread.isSaved ? "Click to unsave" : "Save thread"}
                >
                  {thread.isSaved ? (
                    <BookmarkFillIcon size={14} />
                  ) : (
                    <FiBookmark size={14} />
                  )}
                  <span>{thread.isSaved ? "Saved" : "Save"}</span>
                </motion.button>
              )}
              {thread.lastReply && (
                <div className="tc-last-reply">
                  <span>Last reply by</span>
                  <span className="tc-last-reply-author">
                    {thread.lastReply.authorInfo?.name}
                  </span>
                  <span>{formatTimeAgo(thread.lastReply.createdAt)}</span>
                </div>
              )}
            </div>

            {canInteract && !isReadOnly && (
              <ActionsRow
                communityId={communityId}
                threadId={thread._id}
                isFollowing={thread.isFollowing}
                upvoteCount={thread.upvoteCount}
                replyCount={thread.replyCount}
                onAward={handleAward}
                onReport={handleReport}
              />
            )}
          </div>
        </div>
      </motion.article>

      {/* Modals */}
      <AnimatePresence>
        {showReportModal && (
          <Suspense fallback={null}>
            <ReportThreadModal
              isOpen={showReportModal}
              onClose={() => setShowReportModal(false)}
              communityId={communityId}
              threadId={thread._id}
              threadTitle={thread.title}
            />
          </Suspense>
        )}

        {showAwardModal && (
          <Suspense fallback={null}>
            <AwardThreadModal
              isOpen={showAwardModal}
              onClose={() => setShowAwardModal(false)}
              communityId={communityId}
              threadId={thread._id}
              threadTitle={thread.title}
              userReactions={thread.userReactions || []}
              onAwardGiven={(type, data) => {
                // Update thread with new reaction data
                if (data?.reaction) {
                  const newReactionCounts = { ...(thread.reactionCounts || {}) };
                  newReactionCounts[type] = (newReactionCounts[type] || 0) + 1;
                  const newUserReactions = [...(thread.userReactions || []), type];
                  onThreadUpdated?.({
                    ...thread,
                    reactionCounts: newReactionCounts,
                    totalReactions: (thread.totalReactions || 0) + 1,
                    userReactions: newUserReactions,
                  });
                } else if (data) {
                  onThreadUpdated?.({ ...thread, ...data });
                }
              }}
            />
          </Suspense>
        )}

        {showFlairModal && (
          <Suspense fallback={null}>
            <SetFlairModal
              isOpen={showFlairModal}
              onClose={() => setShowFlairModal(false)}
              communityId={communityId}
              threadId={thread._id}
              threadTitle={thread.title}
              currentFlair={thread.flair}
              onFlairSet={(flair) => onThreadUpdated?.({ ...thread, flair })}
            />
          </Suspense>
        )}

        {showCrossPostModal && (
          <Suspense fallback={null}>
            <CrossPostModal
              isOpen={showCrossPostModal}
              onClose={() => setShowCrossPostModal(false)}
              communityId={communityId}
              threadId={thread._id}
              threadTitle={thread.title}
              threadDescription={thread.description}
            />
          </Suspense>
        )}

        {showEditModal && (
          <Suspense fallback={null}>
            <EditThreadModal
              communityId={communityId}
              threadId={thread._id}
              initialThread={thread}
              onClose={() => setShowEditModal(false)}
              onThreadUpdated={onThreadUpdated}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Image Lightbox - Click to expand thread images */}
      <ImageLightbox
        isOpen={showImageLightbox}
        imageUrl={lightboxImageUrl}
        alt={`${thread.title} - attached image`}
        onClose={handleCloseLightbox}
        showDownload={true}
      />
    </>
  );
};

// Memoize with custom comparison
export default React.memo(ThreadCard, (prev, next) => {
  return (
    prev.thread._id === next.thread._id &&
    prev.thread.upvoteCount === next.thread.upvoteCount &&
    prev.thread.downvoteCount === next.thread.downvoteCount &&
    prev.thread.replyCount === next.thread.replyCount &&
    prev.thread.viewCount === next.thread.viewCount &&
    prev.thread.isPinned === next.thread.isPinned &&
    prev.thread.status === next.thread.status &&
    prev.thread.userHasUpvoted === next.thread.userHasUpvoted &&
    prev.thread.userHasDownvoted === next.thread.userHasDownvoted &&
    prev.thread.isSaved === next.thread.isSaved &&
    prev.thread.isFollowing === next.thread.isFollowing &&
    JSON.stringify(prev.thread.flair) === JSON.stringify(next.thread.flair) &&
    // Award/Reaction data comparison
    prev.thread.totalReactions === next.thread.totalReactions &&
    JSON.stringify(prev.thread.reactionCounts) === JSON.stringify(next.thread.reactionCounts) &&
    JSON.stringify(prev.thread.userReactions) === JSON.stringify(next.thread.userReactions) &&
    prev.isModeratorOrCreator === next.isModeratorOrCreator &&
    prev.isReadOnly === next.isReadOnly &&
    prev.canInteract === next.canInteract
  );
});
